/**
 * Linha de variacao contra o periodo anterior: glifo + texto, SEMPRE.
 *
 * Mesmas regras dos KPIs do funil: cor e reforco, nunca o unico canal; sem
 * periodo anterior o texto diz isso em vez de inventar "0%"; a taxa compara
 * "de X% para Y%" em vez de dividir taxa por taxa. `sentido` decide se subir
 * e bom (recuperados), ruim (esgotados) ou neutro (abandonaram: mais gente
 * para recuperar, mas mais gente que saiu).
 */

import { formatPercentFromBps } from '../../../lib/format';

export type SentidoDoDelta = 'neutro' | 'maior-melhor' | 'menor-melhor';

export interface DeltaCalculado {
  readonly atual: number;
  readonly anterior: number;
  readonly diferenca: number;
  /** Variacao relativa em bps (inteiro), `null` quando o anterior e zero. */
  readonly bps: number | null;
}

export function delta(atual: number, anterior: number): DeltaCalculado {
  const diferenca = atual - anterior;
  return { atual, anterior, diferenca, bps: anterior === 0 ? null : Math.round((diferenca * 10_000) / anterior) };
}

function classeDoDelta(diferenca: number, sentido: SentidoDoDelta): string {
  if (diferenca === 0 || sentido === 'neutro') {
    return 'text-ink-muted';
  }
  const bom = sentido === 'maior-melhor' ? diferenca > 0 : diferenca < 0;
  return bom ? 'text-status-good' : 'text-status-critical';
}

function glifo(diferenca: number): string {
  if (diferenca > 0) {
    return '▲';
  }
  if (diferenca < 0) {
    return '▼';
  }
  return '▶';
}

function comSinal(texto: string, diferenca: number): string {
  if (diferenca > 0) {
    return `+${texto}`;
  }
  if (diferenca < 0) {
    return `−${texto}`;
  }
  return texto;
}

export function LinhaDelta({
  d,
  sentido,
  formatar,
  alinhar = 'esquerda',
}: {
  readonly d: DeltaCalculado | null;
  readonly sentido: SentidoDoDelta;
  /** Recebe o valor ABSOLUTO da diferenca (e o anterior). */
  readonly formatar: (abs: number) => string;
  readonly alinhar?: 'esquerda' | 'centro';
}): JSX.Element {
  const alinhamento = alinhar === 'centro' ? 'justify-center' : '';
  if (d === null) {
    return <p className={`flex ${alinhamento} text-xs text-ink-subtle`}>Sem período anterior para comparar</p>;
  }
  if (d.diferenca === 0) {
    return (
      <p className={`flex items-center ${alinhamento} gap-1 text-xs text-ink-muted`}>
        <span aria-hidden="true">{glifo(0)}</span>
        <span>Igual ao período anterior ({formatar(d.anterior)})</span>
      </p>
    );
  }
  const relativo = d.bps === null ? null : comSinal(formatPercentFromBps(Math.abs(d.bps)), d.diferenca);
  const absoluto = comSinal(formatar(Math.abs(d.diferenca)), d.diferenca);
  return (
    <p className={`flex flex-wrap items-center ${alinhamento} gap-x-1 text-xs tabular-nums ${classeDoDelta(d.diferenca, sentido)}`}>
      <span aria-hidden="true">{glifo(d.diferenca)}</span>
      <span className="sr-only">{d.diferenca > 0 ? 'subiu' : 'caiu'}</span>
      {relativo === null ? null : <strong className="font-semibold">{relativo}</strong>}
      {relativo === null ? null : <span aria-hidden="true">·</span>}
      <span>
        {absoluto} <span className="text-ink-subtle">vs. período anterior ({formatar(d.anterior)})</span>
      </span>
    </p>
  );
}
